import React, { useEffect, useCallback, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { HiChevronDoubleRight } from 'react-icons/hi'
import { RiDeleteBin6Line } from 'react-icons/ri'
import DatePicker from 'react-datepicker'
import {
  fetchTask,
  updateTask,
  deleteTask,
  fetchTaskComments,
  addTaskComment,
  sendReminder,
} from '../../Api/task'
import Dropdown from '../Dropdown/Dropdown'
import 'react-datepicker/dist/react-datepicker.css'
import './task.css'
import { toast } from 'react-toastify'

const Task = ({ assignees, tags, priorities, fetchTasksData }) => {
  const tage = tags.map(({ label }) => label)
  const result = tage.map((label, i) => ({ label, value: tage[i] }))
  const priorityButtonRef = useRef(null)
  const assigneeButtonRef = useRef(null)
  const tagButtonRef = useRef(null)
  const statusButtonRef = useRef(null)
  const privateButtonRef = useRef(null)
  const notifyMembersButtonRef = useRef(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [priority, setPriority] = useState('')
  const [assignee, setAssignee] = useState([])
  const [tag, setTag] = useState([])
  const [status, setStatus] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [notifyMembers, setNotifyMembers] = useState([])
  const [comments, setComments] = useState([])
  const [comment, setComment] = useState('')
  const [state, setState] = useState({
    title: '',
    dueDate: '',
    description: '',
  })
  const monthsShort = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const fetchCommentsData = useCallback(async () => {
    const apiResponse = await fetchTaskComments(searchParams.get('task'))
    if (apiResponse) {
      setComments(apiResponse)
    } else {
      setComments([])
    }
  }, [setComments, searchParams])
  const fetchData = useCallback(async () => {
    const apiResponse = await fetchTask(searchParams.get('task'))
    if (apiResponse[0]) {
      setState({
        title: apiResponse[0].title,
        dueDate: apiResponse[0].dueDate
          ? apiResponse[0].dueDate.split('T')[0]
          : '',
        description: apiResponse[0].description
          ? apiResponse[0].description
          : '',
        isPrivate: false,
      })
      setPriority(apiResponse[0].priority)
      setAssignee([
        ...apiResponse[0].assignee
          .filter((item) => item.email)
          .map((item) => item.email),
      ])
      const taskTag = apiResponse[0]?.tag?.filter((item) => item?.tag) || []
      setTag([...taskTag.map((item) => item.tag)])
      setStatus(apiResponse[0].status)
      setData(apiResponse[0])
      setIsPrivate(apiResponse[0].isPrivate)
      setNotifyMembers([
        ...apiResponse[0].notifyMembers
          .filter((item) => item.email)
          .map((item) => item.email),
      ])
    } else {
      setData(null)
    }
  }, [searchParams])
  useEffect(() => {
    if (searchParams.get('updated')) {
      navigate(
        `/tasks/?tag=${searchParams.get('tag')}&task=${searchParams.get(
          'task'
        )}&card=`
      )
    } else {
      if (searchParams.get('task')) {
        fetchData()
        fetchCommentsData()
      }
    }
  }, [searchParams, navigate, fetchData, fetchCommentsData])
  const changeHandler = useCallback((event) => {
    setState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }))
  }, [])

  const updateTaskHandler = useCallback(() => {
    if (data) {
      const cleanData = {
        title: state.title.trim(),
        description: state.description.trim(),
        dueDate: state.dueDate,
        isPrivate: isPrivate,
        assignee:
          assignee?.length > 0
            ? [
              ...assignee.map((email) => ({
                email: email,
              })),
            ]
            : [],
        tag:
          tag.filter((tag) => tag)?.length > 0
            ? [
              ...tag
                .filter((tag) => tag)
                .map((tag) => ({
                  tag: tag,
                })),
            ]
            : [],
        notifyMembers:
          notifyMembers?.length > 0
            ? [
              ...notifyMembers.map((email) => ({
                email: email,
              })),
            ]
            : [],
        priority: priority,
        status: status,
      }
      if (cleanData.title === '') {
        toast.error('Please fill the title of the task')
      } else if (!cleanData.dueDate) {
        toast.error('Please fill the due date of the task')
      } else if (cleanData.assignee?.length <= 0) {
        toast.error('Please select atleast one assignee for the task')
      } else {
        updateTask(data._id, cleanData)
        fetchTasksData()
      }
    }
  }, [
    assignee,
    tag,
    state,
    status,
    priority,
    isPrivate,
    data,
    fetchTasksData,
    notifyMembers,
  ])

  useEffect(() => {
    const updateData = setTimeout(() => {
      updateTaskHandler()
    }, 1000)
    return () => clearTimeout(updateData)
  }, [
    assignee,
    tag,
    priority,
    status,
    state,
    updateTaskHandler,
    isPrivate,
    notifyMembers,
    data,
  ])
  const deleteTaskHandler = useCallback(async () => {
    setShowDeleteModal(false)
    const apiResponse = await deleteTask(data._id)
    if (apiResponse) {
      toast.success('Task deleted successfully')
      navigate(
        `/tasks/?tag=${
          searchParams.get('tag') &&
          searchParams.get('tag') !== 'null' &&
          searchParams.get('tag')
        }&task=&card=`
      )
    } else {
      toast.error('Some error occured')
    }
  }, [data, searchParams, navigate])

  const addCommentHandler = useCallback(async () => {
    if (comment.trim()) {
      const apiResponse = await addTaskComment(searchParams.get('task'), {
        html: comment.trim(),
      })
      if (apiResponse) {
        setComment('')
        await fetchCommentsData()
      }
    }
  }, [comment, searchParams, fetchCommentsData])

  const sendReminderHandler = useCallback(async () => {
    if (notifyMembers?.length > 0 && searchParams.get('task')) {
      const apiResponse = await sendReminder(searchParams.get('task'))
      if (apiResponse) {
        toast.success('Reminder sent successfully')
      }
    } else {
      toast.error('Please select members to send reminder')
    }
  }, [notifyMembers, searchParams])
  return (
    <div className={searchParams.get('task') ? 'task active' : 'task'}>
      {showDeleteModal && (
        <div className='delete-task'>
          <div className='cancel'>
            <button onClick={() => setShowDeleteModal(false)}>X</button>
          </div>
          <h3>Are you sure you want to delete?</h3>
          <p>This action may notify other members. </p>
          {/* <div className="dont-show">
            <button />
            <span>Don't show this again</span>
          </div> */}
          <div className='actions'>
            <button onClick={() => setShowDeleteModal(false)}>
              No, Cancel
            </button>
            <button onClick={deleteTaskHandler}>Yes, Confirm</button>
          </div>
        </div>
      )}
      <div className='actions'>
        <button
          onClick={() =>
            navigate(
              `/tasks/?tag=${
                searchParams.get('tag') &&
                searchParams.get('tag') !== 'null' &&
                searchParams.get('tag')
              }&task=&card=`
            )
          }
        >
          <HiChevronDoubleRight size={16} />
        </button>
        <button>
          <RiDeleteBin6Line
            onClick={() => setShowDeleteModal(true)}
            size={16}
          />
        </button>
      </div>
      <div className='content'>
        <textarea
          name='title'
          onChange={changeHandler}
          className='title'
          spellCheck={false}
          rows={3}
          value={state?.title}
        />
        <ul>
          <li className='duedate' value={state.dueDate}>
            <div className='header'>
              📅 <span>Due date</span>
            </div>
            <p
              className={
                state?.dueDate &&
                (new Date(state.dueDate).getTime() < new Date().getTime()
                  ? 'overdue'
                  : new Date(state.dueDate).getTime() >=
                    new Date().getTime() - 7 * 86400000
                    ? 'warn'
                    : '')
              }
            >
              {state?.dueDate &&
                `${months[new Date(state?.dueDate).getMonth()]} ${new Date(
                  state?.dueDate
                ).getDate()}, ${new Date(state?.dueDate).getFullYear()}`}
            </p>
            <DatePicker
              selected={state.dueDate ? new Date(state.dueDate) : new Date()}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  dueDate: new Date(e),
                }))
              }
              dateFormat='Pp'
              minDate={new Date()}
              maxDate={
                new Date(new Date().getTime() + 60 * 60 * 24 * 1000 * 365)
              }
            />
          </li>
          <li>
            <div className='header'>
              🚦 <span>Priority</span>
            </div>
            <div className='filter'>
              <button
                className={
                  priority ? 'tag-filter-button active' : 'tag-filter-button'
                }
                ref={priorityButtonRef}
              >
                {priority ? priority.split('_')[1] : 'Empty'}
              </button>
              <Dropdown
                isMultiple={false}
                data={priorities}
                setActiveFilters={setPriority}
                activeFilters={priority}
                ref={priorityButtonRef}
              />
            </div>
          </li>
          <li className='assignee'>
            <div className='header'>
              🙋‍♀️ <span>Assignee</span>
            </div>
            <div className='filter'>
              <button
                className={
                  assignee?.length > 0
                    ? 'tag-filter-button active'
                    : 'tag-filter-button'
                }
                ref={assigneeButtonRef}
              >
                {assignee?.length > 0 ? assignee.join(', ') : 'Empty'}
              </button>
              <Dropdown
                isMultiple={true}
                isSearchable={true}
                data={assignees}
                setActiveFilters={setAssignee}
                activeFilters={assignee}
                ref={assigneeButtonRef}
              />
            </div>
          </li>
          <li>
            <div className='header'>
              {isPrivate ? '🔒' : '🔓'} <span>Nature</span>
            </div>
            <div className='filter'>
              <button
                className={
                  priority ? 'tag-filter-button active' : 'tag-filter-button'
                }
                ref={privateButtonRef}
              >
                {isPrivate ? 'Private' : 'Shared'}
              </button>
              <Dropdown
                isMultiple={false}
                data={[
                  {
                    value: true,
                    label: 'Private',
                  },
                  {
                    value: false,
                    label: 'Shared',
                  },
                ]}
                setActiveFilters={setIsPrivate}
                activeFilters={isPrivate}
                ref={privateButtonRef}
              />
            </div>
          </li>
          <li className='static'>
            <div className='header'>
              👥 <span>Meeting</span>
            </div>
            <p>{data?.meetingTag}</p>
            <p></p>
          </li>
          <li className='assignee'>
            <div className='header'>
              🏷️ <span>Tags</span>
            </div>
            <div className='filter'>
              <button
                className={
                  tag?.length > 0
                    ? 'tag-filter-button active'
                    : 'tag-filter-button'
                }
                ref={tagButtonRef}
              >
                {tag?.length > 0 ? tag.join(', ') : 'Empty'}
              </button>
              <Dropdown
                isMultiple={true}
                isSearchable={true}
                data={result}
                setActiveFilters={setTag}
                activeFilters={tag}
                ref={tagButtonRef}
              />
            </div>
          </li>
          <li>
            <div className='header'>
              ⚙️ <span>Status</span>
            </div>
            <div className='filter'>
              <button
                className={
                  status ? 'tag-filter-button active' : 'tag-filter-button'
                }
                ref={statusButtonRef}
              >
                {status
                  ? status === 'IN_PROGRESS'
                    ? 'IN PROGRESS'
                    : status
                  : 'Empty'}
              </button>
              <Dropdown
                isMultiple={false}
                data={[
                  { value: 'OPEN', label: 'OPEN' },
                  { value: 'IN_PROGRESS', label: 'IN PROGRESS' },
                  { value: 'DONE', label: 'DONE' },
                ]}
                setActiveFilters={setStatus}
                activeFilters={status}
                ref={statusButtonRef}
              />
            </div>
          </li>
          <li className='assignee'>
            <div className='header'>
              🔔 <span>Reminder</span>
            </div>
            <div className='filter'>
              <button
                className={
                  notifyMembers?.length > 0
                    ? 'tag-filter-button active'
                    : 'tag-filter-button'
                }
                ref={notifyMembersButtonRef}
              >
                {notifyMembers?.length > 0 ? notifyMembers.join(', ') : 'Empty'}
              </button>
              <Dropdown
                isMultiple={true}
                isSearchable={true}
                data={assignees}
                setActiveFilters={setNotifyMembers}
                activeFilters={notifyMembers}
                ref={notifyMembersButtonRef}
              />
              <button className='reminder' onClick={sendReminderHandler}>
                Send reminder now
              </button>
            </div>
          </li>
        </ul>
        <div className='comments'>
          <div className='header'>
            <span>💬 Comments</span>
          </div>
          {comments?.length > 0 && (
            <ul>
              {comments.map((item) => (
                <li key={item._id}>
                  <div>
                    <h4>{item.userName.split(' ')[0]}</h4>
                    <span>{`${
                      monthsShort[new Date(item.createdAt).getMonth()]
                    } ${new Date(item.createdAt).getDate()}`}</span>
                  </div>
                  <p>{item.html}</p>
                </li>
              ))}
            </ul>
          )}
          <textarea
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            value={comment}
            placeholder='Leave a comment...'
          />
          <button onClick={addCommentHandler}>Add comment</button>
        </div>
        <div className='description'>
          <span>
            📝 <em>description</em>
          </span>
          <textarea
            name='description'
            onChange={changeHandler}
            spellCheck={false}
            rows={3}
            value={state?.description}
          />
        </div>
      </div>
    </div>
  )
}

export default Task
